import React, { useState } from 'react';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { AssetClassMeta } from '../types';
import { isUUID } from '../utils/storage';
import { v4 } from '../utils/uuid';

function generateClassId(name: string): string {
  return name
    .replace(/[đĐ]/g, 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

const emptyForm = (): Partial<AssetClassMeta> => ({
  id: '', name: '', color: '#10b981', isFixedIncome: false,
});

interface InlineFormProps {
  value: Partial<AssetClassMeta>;
  onChange: (v: Partial<AssetClassMeta>) => void;
  onSave: () => void;
  onCancel: () => void;
  nameEditable?: boolean;
  idAutoFill?: boolean;
}

function InlineForm({ value, onChange, onSave, onCancel, nameEditable = true, idAutoFill = false }: InlineFormProps) {
  const f = <K extends keyof AssetClassMeta>(k: K, v: AssetClassMeta[K]) => onChange({ ...value, [k]: v });

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Color swatch picker */}
      <label style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          backgroundColor: value.color || '#94a3b8',
          border: '2px solid rgba(255,255,255,0.15)',
          boxShadow: `0 0 0 2px ${value.color || '#94a3b8'}40`,
        }} />
        <input type="color" value={value.color || '#94a3b8'} onChange={e => f('color', e.target.value)}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
      </label>

      {/* Name */}
      <input
        value={value.name || ''}
        disabled={!nameEditable}
        placeholder="Tên loại tài sản"
        onChange={e => {
          const name = e.target.value;
          const next: Partial<AssetClassMeta> = { ...value, name };
          if (idAutoFill) next.id = generateClassId(name);
          onChange(next);
        }}
        style={{
          flex: 2, minWidth: 120, padding: '7px 10px', borderRadius: 8, fontSize: 13,
          outline: 'none', background: 'var(--bg-input)', border: '1px solid var(--bd)',
          color: nameEditable ? 'var(--tx-1)' : 'var(--tx-3)',
        }}
      />

      {/* ID (slug) */}
      <input
        value={value.id || ''}
        placeholder="id (slug)"
        onChange={e => onChange({ ...value, id: e.target.value })}
        style={{
          flex: 1, minWidth: 90, padding: '7px 10px', borderRadius: 8, fontSize: 12,
          outline: 'none', background: 'var(--bg-input)', border: '1px solid var(--bd)',
          color: 'var(--tx-3)', fontFamily: 'monospace',
        }}
      />

      {/* Fixed income toggle */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0 }}>
        <input type="checkbox" checked={!!value.isFixedIncome} onChange={e => f('isFixedIncome', e.target.checked)}
          style={{ width: 14, height: 14, cursor: 'pointer' }} />
        <span style={{ fontSize: 11, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>Cố định</span>
      </label>

      {/* Actions */}
      <button onClick={onSave} style={{ width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer', background: 'rgba(16,185,129,0.12)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Check size={14} />
      </button>
      <button onClick={onCancel} style={{ width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer', background: 'var(--bg-card-el)', color: 'var(--tx-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <X size={14} />
      </button>
    </div>
  );
}

interface Props { open: boolean; onClose: () => void }

export function AssetClassManager({ open, onClose }: Props) {
  const { state, dispatch } = useAppState();
  const { assetClasses = [], assets } = state;

  const [addForm, setAddForm] = useState<Partial<AssetClassMeta>>(emptyForm());
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<AssetClassMeta>>({});
  const [blockMsg, setBlockMsg] = useState<string | null>(null);

  const openEdit = (c: AssetClassMeta) => { setEditId(c.id); setEditForm({ ...c }); };
  const cancelEdit = () => { setEditId(null); setEditForm({}); };

  const handleAdd = () => {
    if (!addForm.name?.trim() || !addForm.id?.trim()) return;
    if (assetClasses.some(c => c.id === addForm.id)) {
      alert(`ID "${addForm.id}" đã tồn tại.`); return;
    }
    // Đảm bảo ID luôn là UUID để class sync được lên DB
    const payload: AssetClassMeta = {
      ...(addForm as AssetClassMeta),
      id: isUUID(addForm.id) ? addForm.id! : v4(),
    };
    dispatch({ type: 'ADD_ASSET_CLASS', payload });
    setAddForm(emptyForm());
    setAddOpen(false);
  };

  const handleUpdate = () => {
    if (!editForm.name?.trim()) return;
    dispatch({ type: 'UPDATE_ASSET_CLASS', payload: editForm as AssetClassMeta });
    cancelEdit();
  };

  const handleDelete = (c: AssetClassMeta) => {
    const inUse = assets.filter(a => a.assetClass === c.name || a.assetClassId === c.id);
    if (inUse.length > 0) {
      const names = inUse.slice(0, 3).map(a => a.name).join(', ');
      const more = inUse.length > 3 ? ` và ${inUse.length - 3} tài sản khác` : '';
      setBlockMsg(
        `Không thể xóa "${c.name}" — đang có ${inUse.length} tài sản bên trong: ${names}${more}. Hãy di chuyển tài sản sang lớp khác trước.`
      );
      return;
    }
    setBlockMsg(null);
    dispatch({ type: 'DELETE_ASSET_CLASS', payload: c.id });
  };

  return (
    <Modal open={open} onClose={onClose} title="Quản lý danh mục" subtitle="Thêm · Sửa · Xóa lớp tài sản" size="md">

      {/* Block message banner */}
      {blockMsg && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14,
          padding: '11px 14px', borderRadius: 10,
          background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)',
        }}>
          <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>⛔</span>
          <span style={{ fontSize: 12, color: '#fca5a5', lineHeight: 1.5 }}>{blockMsg}</span>
          <button onClick={() => setBlockMsg(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-5)', fontSize: 14, flexShrink: 0, padding: 0 }}>
            ✕
          </button>
        </div>
      )}

      {/* Header columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 90px 60px 64px', gap: 8, padding: '0 4px 8px', borderBottom: '1px solid var(--bd)', marginBottom: 8 }}>
        {['Màu', 'Tên', 'ID', 'Cố định', ''].map(h => (
          <div key={h} style={{ fontSize: 10, color: 'var(--tx-4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
        ))}
      </div>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
        {assetClasses.map(c => {
          const usageCount = assets.filter(a => a.assetClass === c.name || a.assetClassId === c.id).length;
          const isEditing = editId === c.id;

          if (isEditing) {
            return (
              <div key={c.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <InlineForm value={editForm} onChange={setEditForm} onSave={handleUpdate} onCancel={cancelEdit} />
              </div>
            );
          }

          return (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
              borderRadius: 10, background: 'var(--bg-card-el)', border: '1px solid var(--bd)',
              transition: 'background 0.1s',
            }}>
              {/* Color dot */}
              <div style={{ width: 16, height: 16, borderRadius: 5, backgroundColor: c.color, flexShrink: 0, boxShadow: `0 0 0 2px ${c.color}30` }} />

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{c.name}</span>
                {usageCount > 0 && (
                  <span style={{ marginLeft: 7, fontSize: 10, color: 'var(--tx-4)', background: 'var(--bd)', padding: '1px 6px', borderRadius: 4 }}>
                    {usageCount} tài sản
                  </span>
                )}
              </div>

              {/* ID */}
              <code style={{ fontSize: 10, color: 'var(--tx-4)', fontFamily: 'monospace', flexShrink: 0 }}>{c.id}</code>

              {/* Fixed income badge */}
              <div style={{ width: 60, textAlign: 'center', flexShrink: 0 }}>
                {c.isFixedIncome && (
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontWeight: 600 }}>✓</span>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button onClick={() => openEdit(c)} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--tx-4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pencil size={12} />
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  disabled={usageCount > 0}
                  title={usageCount > 0 ? `Không thể xóa — đang có ${usageCount} tài sản` : 'Xóa lớp tài sản'}
                  style={{ width: 28, height: 28, borderRadius: 7, border: 'none', cursor: usageCount > 0 ? 'not-allowed' : 'pointer', background: 'transparent', color: usageCount > 0 ? 'var(--tx-6)' : '#f87171', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: usageCount > 0 ? 0.4 : 1 }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add new */}
      {addOpen ? (
        <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px dashed rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.04)' }}>
          <InlineForm value={addForm} onChange={setAddForm} onSave={handleAdd} onCancel={() => { setAddOpen(false); setAddForm(emptyForm()); }} idAutoFill />
          <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 8 }}>
            * Tick "Cố định" để hiện thêm trường lãi suất &amp; đáo hạn trong form nhập tài sản.
          </div>
        </div>
      ) : (
        <Button variant="secondary" size="sm" icon={<Plus size={12} />} onClick={() => setAddOpen(true)}>
          Thêm loại tài sản
        </Button>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
        <Button variant="ghost" onClick={onClose}>Đóng</Button>
      </div>
    </Modal>
  );
}
